import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Indicador, Selo } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { SeloVerticais, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { data, dinheiro, mesAno, nomeParceiro, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useContrato, useMrr } from './dados';
import { DetalheContrato, textoRecorrencia } from './DetalheContrato';
import { FormContrato } from './FormContrato';
import { CAMINHO_CONTRATOS, COR_ESTADO, ESTADOS, type ContratoResumo } from './tipos';

/** Resumo do MRR (R3) da moeda do workspace: MRR, ARR, pausado, a iniciar e o movimento do mês. */
function ResumoMrr() {
  const { t } = useTranslation('assinaturas');
  const s = useSessao();
  const mrr = useMrr();
  const linha = mrr.data?.por_moeda.find((l) => l.moeda === s.workspace.moeda) ?? mrr.data?.por_moeda[0];
  if (mrr.isLoading || !linha || !linha.total) return null;
  return (
    <section aria-label={t('mrr.titulo')} className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Indicador
        rotulo={t('mrr.titulo')}
        valor={dinheiro(linha.mrr_centavos, linha.moeda)}
        detalhe={
          linha.inadimplentes
            ? `${t('mrr.contratos', { count: linha.contratos })}; ${t('mrr.inadimplentes', { count: linha.inadimplentes, valor: dinheiro(linha.inadimplente_centavos ?? 0, linha.moeda) })}`
            : t('mrr.contratos', { count: linha.contratos })
        }
        cor="verde"
      />
      <Indicador rotulo={t('mrr.arr')} valor={dinheiro(linha.arr_centavos, linha.moeda)} />
      <Indicador rotulo={t('mrr.pausado')} valor={dinheiro(linha.pausado_centavos, linha.moeda)} detalhe={`${t('mrr.aIniciar')}: ${dinheiro(linha.a_iniciar_centavos, linha.moeda)}`} cor={linha.pausado_centavos ? 'laranja' : 'tinta-1'} />
      <Indicador
        rotulo={t('mrr.movimento')}
        valor={dinheiro(linha.novo_centavos + linha.expansao_centavos + (linha.reativacao_centavos ?? 0) - linha.churn_centavos, linha.moeda)}
        detalhe={`${t('mrr.detalheMovimento', {
          novo: dinheiro(linha.novo_centavos, linha.moeda),
          expansao: dinheiro(linha.expansao_centavos, linha.moeda),
          churn: dinheiro(linha.churn_centavos, linha.moeda),
        })}${linha.reativacao_centavos ? `, ${t('mrr.reativacao', { valor: dinheiro(linha.reativacao_centavos, linha.moeda) })}` : ''}${
          linha.churn_centavos && linha.inicial_centavos > 0 ? `; ${t('mrr.taxaChurn', { taxa: `${numero((linha.churn_centavos * 100) / linha.inicial_centavos, 1)}%` })}` : ''
        }`}
      />
    </section>
  );
}

/**
 * Drawer do contrato: o novo abre direto no formulário; o salvo abre na ficha (Editar troca para o formulário). Também é
 * usado pela aba Reajustes ("Editar contrato" abre o contrato sobre a tela).
 */
export function DrawerContrato({ alvo, aoFechar, editarDireto = false }: { alvo: string | 'novo' | null; aoFechar: () => void; editarDireto?: boolean }) {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [id, setId] = useState<string | null>(alvo && alvo !== 'novo' ? alvo : null);
  const [editando, setEditando] = useState(alvo === 'novo' || editarDireto);
  const contrato = useContrato(id);
  const novo = !id;
  const titulo = novo ? t('acoes.novo') : contrato.data ? contrato.data.numero : t('abas.contratos');
  const idForm = `form-contrato-${id ?? 'novo'}`;
  const podeSalvar = novo ? s.pode('assinaturas.criar') : s.pode('assinaturas.editar');
  return (
    <Drawer
      aberto={!!alvo}
      aoFechar={aoFechar}
      largura="lg"
      titulo={titulo}
      rodape={
        editando && podeSalvar ? (
          <>
            <Botao variante="fantasma" onClick={() => (novo ? aoFechar() : setEditando(false))}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form={idForm}>
              {tn('comum.salvar')}
            </Botao>
          </>
        ) : undefined
      }
    >
      {novo ? (
        <FormContrato
          contrato={null}
          idForm={idForm}
          aoSalvar={(nid) => {
            setId(nid);
            setEditando(false);
          }}
        />
      ) : contrato.isLoading ? (
        <Carregando />
      ) : contrato.isError || !contrato.data ? (
        <Erro erro={contrato.error} aoTentar={() => void contrato.refetch()} />
      ) : editando ? (
        <FormContrato key={`${contrato.data.id}-editar`} contrato={contrato.data} idForm={idForm} aoSalvar={() => setEditando(false)} />
      ) : (
        <DetalheContrato contrato={contrato.data} aoEditar={() => setEditando(true)} aoExcluir={aoFechar} />
      )}
    </Drawer>
  );
}

/** Aba Contratos (W12.11): resumo do MRR, lista com saúde do faturamento, filtros, importação e o drawer do contrato. */
export function Contratos() {
  const { t } = useTranslation('assinaturas');
  const s = useSessao();
  // Fase 2 (W22, K8): link do aviso "contrato perto do fim" (`?contrato=<id>`) abre a ficha, também com a aba já aberta.
  const [busca, setBusca] = useSearchParams();
  const [aberto, setAberto] = useState<string | 'novo' | null>(busca.get('contrato'));
  useEffect(() => {
    const id = busca.get('contrato');
    if (id) setAberto(id);
  }, [busca]);
  const fechar = () => {
    setAberto(null);
    if (busca.get('contrato'))
      setBusca(
        (atual) => {
          const n = new URLSearchParams(atual);
          n.delete('contrato');
          return n;
        },
        { replace: true },
      );
  };
  const [importar, setImportar] = useState(false);
  const [estado, setEstado] = useState('');
  const [saude, setSaude] = useState('');
  const { t: tn } = useTranslation();

  const catalogo = useEmpresasDoCatalogo().data?.empresas ?? [];
  const colunas: Coluna<ContratoResumo>[] = [
    {
      chave: 'numero',
      rotulo: t('campos.numero'),
      principal: true,
      ordenavel: true,
      render: (c) => (
        <span className="flex min-w-0 flex-col">
          <span className="font-mono font-medium">{c.numero}</span>
          <span className="truncate text-xs text-tinta-3" title={nomeParceiro(c.parceiro_nome)}>
            {nomeParceiro(c.parceiro_nome)}
          </span>
        </span>
      ),
    },
    // 22/10 (Gustavo): a vertical (empresa) do contrato, com duas empresas ou mais.
    ...(catalogo.length > 1 ? [{ chave: 'empresa_id', rotulo: tn('verticais.titulo'), render: (c: ContratoResumo) => <SeloVerticais empresas={c.empresa_id ? [c.empresa_id] : []} catalogo={catalogo} /> }] : []),
    { chave: 'tipo', rotulo: t('campos.intervalo'), render: (c) => textoRecorrencia(t, c) },
    { chave: 'valor_ciclo_centavos', rotulo: t('campos.valorCiclo'), alinhar: 'direita', mono: true, render: (c) => dinheiro(c.valor_ciclo_centavos, c.moeda) },
    { chave: 'mrr_centavos', rotulo: t('campos.mrr'), alinhar: 'direita', mono: true, render: (c) => (c.tipo === 'recorrente' && (c.estado === 'ativo' || c.estado === 'pausado') ? dinheiro(c.mrr_centavos, c.moeda) : '') },
    {
      chave: 'proxima_competencia',
      rotulo: t('campos.proxima'),
      mono: true,
      render: (c) => (c.proxima_competencia && c.proxima_geracao ? `${mesAno(c.proxima_competencia)} (${data(c.proxima_geracao)})` : ''),
    },
    {
      chave: 'falhas',
      rotulo: t('campos.saude'),
      render: (c) =>
        c.inadimplente_desde && (c.estado === 'ativo' || c.estado === 'pausado') ? (
          <Selo cor="perigo">{c.falhas ? `${t('inadimplencia.selo')}; ${t('saude.falhas', { count: c.falhas })}` : t('inadimplencia.selo')}</Selo>
        ) : c.falhas ? (
          <Selo cor="perigo">{t('saude.falhas', { count: c.falhas })}</Selo>
        ) : c.estado === 'ativo' ? (
          <Selo cor="verde">{t('saude.emDia')}</Selo>
        ) : (
          ''
        ),
    },
    { chave: 'estado', rotulo: t('campos.estado'), render: (c) => <Selo cor={COR_ESTADO[c.estado]}>{t(`estados.${c.estado}`)}</Selo> },
    { chave: 'plano_nome', rotulo: t('campos.plano'), oculta: true, render: (c) => c.plano_nome ?? '' },
    { chave: 'indice', rotulo: t('campos.indice'), oculta: true, render: (c) => t(`indices.${c.indice}`) },
    { chave: 'referencia_externa', rotulo: t('campos.referencia'), oculta: true, mono: true, render: (c) => c.referencia_externa ?? '' },
    { chave: 'inicio', rotulo: t('campos.inicio'), oculta: true, mono: true, render: (c) => data(c.inicio) },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {(s.pode('assinaturas.criar') || s.pode('assinaturas.excluir')) && (
        <AcaoPrimaria>
          {s.pode('assinaturas.excluir') && <BotaoLixeira entidades={['assinaturas.contrato']} />}
          {s.pode('assinaturas.criar') && (
            <>
              {s.pode('assinaturas.importar') && (
                <>
                  <BotaoModeloCsv caminho={CAMINHO_CONTRATOS} />
                  <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                    {t('acoes.importar')}
                  </Botao>
                </>
              )}
              <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
                {t('acoes.novo')}
              </Botao>
            </>
          )}
        </AcaoPrimaria>
      )}
      <ResumoMrr />
      <Tabela<ContratoResumo>
        tela="assinaturas.contratos"
        caminho={CAMINHO_CONTRATOS}
        params={{ estado: estado || undefined, com_falha: saude === 'falha' ? true : undefined, inadimplente: saude === 'inadimplente' ? true : undefined }}
        filtros={{ estado, saude }}
        aoAplicarFiltros={(f) => {
          setEstado(String(f.estado ?? ''));
          setSaude(String(f.saude ?? ''));
        }}
        colunas={colunas}
        entidade="assinaturas.contrato"
        aoClicar={(c) => setAberto(c.id)}
        vazio={<Vazio texto={t('vazio.contratos')} />}
        filtrosExtra={
          <>
            <div className="w-full sm:w-48">
              <Selecao aria-label={t('filtros.estado')} vazio={t('filtros.todos')} opcoes={ESTADOS.map((e) => ({ valor: e, rotulo: t(`estados.${e}`) }))} value={estado} onChange={(e) => setEstado(e.target.value)} />
            </div>
            <div className="w-full sm:w-48">
              <Selecao aria-label={t('filtros.saude')} vazio={t('filtros.todas')} opcoes={[
                  { valor: 'falha', rotulo: t('filtros.comFalha') },
                  { valor: 'inadimplente', rotulo: t('filtros.inadimplentes') },
                ]} value={saude} onChange={(e) => setSaude(e.target.value)} />
            </div>
          </>
        }
      />
      {aberto && <DrawerContrato key={aberto} alvo={aberto} aoFechar={fechar} />}
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho={CAMINHO_CONTRATOS} titulo={t('acoes.importarTitulo')} />
    </div>
  );
}
